"use strict";
/**
 * Materials-public canonical definition — the single source of truth for the
 * `materials-public` OpenSearch index family.
 *
 * Per the canonical-shared-types model, `shared-types` (the pure, AWS-SDK-free contract
 * package) holds, per index family, BOTH:
 *   (a) the field-name constants (`MATERIAL_FIELDS`) — runtime values typed against
 *       `MaterialSearchDocument` keys so the contract and the constants cannot drift, and
 *   (b) the OpenSearch template body (`buildMaterialsPublicTemplate`) — settings
 *       (INCLUDING the `analysis` analyzers/normalizers), `dynamic: true`, and explicit
 *       `properties`.
 *
 * `MATERIAL_FIELDS` is SHARED by the `materials-public` and `materials-personal` families
 * (both project `MaterialSearchDocument`), per the design. Every other surface (the
 * reindex-script registrar, the indexer writer, the search reader) references these
 * instead of authoring its own copy, so material field names have exactly one source.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MATERIAL_FIELDS = void 0;
exports.buildMaterialsPublicTemplate = buildMaterialsPublicTemplate;
/**
 * Canonical field-name constants for the material index families (`materials-public` and
 * `materials-personal`).
 *
 * Each value is a field name constrained to be a real key of `MaterialSearchDocument` via
 * `satisfies Record<string, keyof MaterialSearchDocument>`, so a typo or a rename that
 * breaks the contract fails to compile. Covers the contract-backed fields the public
 * template indexes. Note `usage_count` and `inventory_count` are mapping-only (not keys of
 * `MaterialSearchDocument`), so they are NOT members here and stay as plain string keys in
 * the template `properties`.
 */
exports.MATERIAL_FIELDS = {
    material_id: 'material_id',
    product_name: 'product_name',
    name: 'name',
    category: 'category',
    brand: 'brand',
    parent_material_id: 'parent_material_id',
    average_rating: 'average_rating',
    review_count: 'review_count',
    is_hidden: 'is_hidden',
    contributed_by: 'contributed_by',
    created_at: 'created_at',
};
/**
 * Build the composable OpenSearch index-template body for the `materials-public` family.
 *
 * Ported VERBATIM from the reindex script's `defaultMaterialsPublicTemplate`, including
 * the full `settings.analysis` block (the `fpw_text` analyzer, the `english_*` filters,
 * and the `lowercase` normalizer) which `product_name`/`name`/`category`/`brand`
 * reference. `mappings.dynamic` is the literal `true` (the BF-018 guard). Property keys
 * that are also `MATERIAL_FIELDS` field names are keyed by the constants so the mapping
 * types exactly the fields the constants name; mapping-only keys (`usage_count`,
 * `inventory_count`) are template-internal and kept as plain string keys.
 */
function buildMaterialsPublicTemplate(opts) {
    return {
        index_patterns: [opts.pattern],
        template: {
            settings: {
                number_of_shards: opts.shards ?? 1,
                number_of_replicas: opts.replicas ?? 0,
                refresh_interval: opts.refresh ?? '5s',
                analysis: {
                    analyzer: {
                        fpw_text: {
                            type: 'custom',
                            tokenizer: 'standard',
                            filter: ['lowercase', 'asciifolding', 'english_possessive_stemmer', 'english_stop', 'english_stemmer'],
                        },
                    },
                    filter: {
                        english_stop: { type: 'stop', stopwords: '_english_' },
                        english_stemmer: { type: 'stemmer', language: 'english' },
                        english_possessive_stemmer: { type: 'stemmer', language: 'possessive_english' },
                    },
                    normalizer: {
                        lowercase: { type: 'custom', char_filter: [], filter: ['lowercase', 'asciifolding'] },
                    },
                },
            },
            mappings: {
                dynamic: true,
                properties: {
                    [exports.MATERIAL_FIELDS.material_id]: { type: 'keyword' },
                    [exports.MATERIAL_FIELDS.product_name]: {
                        type: 'text',
                        analyzer: 'fpw_text',
                        fields: {
                            keyword: { type: 'keyword' },
                            sasy: { type: 'search_as_you_type' },
                        },
                    },
                    [exports.MATERIAL_FIELDS.name]: {
                        type: 'text',
                        analyzer: 'fpw_text',
                        fields: {
                            keyword: { type: 'keyword' },
                            sasy: { type: 'search_as_you_type' },
                        },
                    },
                    [exports.MATERIAL_FIELDS.category]: { type: 'keyword', normalizer: 'lowercase' },
                    [exports.MATERIAL_FIELDS.brand]: { type: 'keyword', normalizer: 'lowercase' },
                    [exports.MATERIAL_FIELDS.parent_material_id]: { type: 'keyword' },
                    [exports.MATERIAL_FIELDS.average_rating]: { type: 'float' },
                    [exports.MATERIAL_FIELDS.review_count]: { type: 'integer' },
                    usage_count: { type: 'integer' },
                    inventory_count: { type: 'integer' },
                    [exports.MATERIAL_FIELDS.is_hidden]: { type: 'boolean' },
                    [exports.MATERIAL_FIELDS.contributed_by]: { type: 'keyword' },
                    [exports.MATERIAL_FIELDS.created_at]: { type: 'date' },
                },
            },
        },
        priority: 100,
        _meta: { managed_by: 'fpw', purpose: 'materials-public' },
    };
}
